import { UrlSerializer, UrlTree, DefaultUrlSerializer } from '@angular/router';

/**
 * Custom URL serializer that converts URLs to lowercase before routing occurs.
 * This ensures that all routes are handled consistently in lowercase format.
 */
export class LowercaseUrlSerializer implements UrlSerializer {
	private defaultUrlSerializer = new DefaultUrlSerializer();

	/**
	 * Parses a URL string and returns a UrlTree with lowercase paths
	 * @param url - The URL string to parse
	 * @returns UrlTree with lowercase segments
	 */
	public parse(url: string): UrlTree {
		// Convert the URL to lowercase before parsing
		// Preserve query parameters and fragments as they might be case-sensitive
		const lowercaseUrl = this.toLowerCaseUrl(url);
		return this.defaultUrlSerializer.parse(lowercaseUrl);
	}

	/**
	 * Serializes a UrlTree back to a URL string
	 * @param tree - The UrlTree to serialize
	 * @returns URL string representation
	 */
	public serialize(tree: UrlTree): string {
		// Use the default serializer and then convert the result to lowercase
		const serialized = this.defaultUrlSerializer.serialize(tree);
		return this.toLowerCaseUrl(serialized);
	}

	/**
	 * Converts only the path segments to lowercase while preserving
	 * query parameters and fragments which might be case-sensitive
	 * @param url - The URL to convert
	 * @returns URL with lowercase path segments
	 */
	private toLowerCaseUrl(url: string): string {
		// Split the URL into path and query/fragment parts
		const urlParts = url.split(/[?#]/);
		const path = urlParts[0];
		const queryAndFragment = url.substring(path.length);

		// Convert only the path to lowercase
		const lowercasePath = path.toLowerCase();

		// Reconstruct the URL with lowercase path but original query/fragment
		return lowercasePath + queryAndFragment;
	}
}
